import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react"
import type { PenKind } from "@/components/sketch/sketch-pen-flyout"
import type { ShapeKind } from "@/components/sketch/sketch-shape-flyout"
import { SketchSelectionOverlay } from "@/components/sketch/sketch-selection-overlay"
import { SketchTextPanel } from "@/components/sketch/sketch-text-panel"
import { SelectDropdown } from "@/components/ui/select-dropdown"
import {
  arrowBBox,
  arrowLabelFontSize,
  arrowLabelOrigin,
  bboxIntersects,
  eraseStrokesAt,
  finalizePenStrokePoints,
  hitTestArrow,
  hitTestArrowLabel,
  hitTestShape,
  hitTestStroke,
  hitTestText,
  newArrowId,
  newShapeId,
  newStrokeId,
  normalizeRect,
  orientedTipRect,
  pathTravelAngle,
  pointInBBox,
  resizeBBox,
  shapeBBox,
  strokeBBox,
  strokeRibbonPolygon,
  textBBox,
  tipThicknessForWidth,
  transformArrowToBBox,
  transformShapeToBBox,
  transformStrokeToBBox,
  transformTextToBBox,
  translateArrow,
  translateShape,
  translateStroke,
  translateText,
  unionBBox,
  type BBox,
  type ResizeHandle,
} from "@/lib/sketch-geometry"
import {
  clampPan,
  fitCamera,
  hairlinePx,
  wheelZoomFactor,
  zoomAt,
  type SketchCamera,
  type SketchZoomPreset,
} from "@/lib/sketch-viewport"
import { cn } from "@/lib/utils"

export type SketchTool = "select" | "pen" | "pan" | "eraser" | "shape" | "arrow" | "arrowLabel" | "text"

export type SketchStroke = {
  id: string
  tool: "pen" | "eraser"
  penKind?: PenKind
  color: string
  width: number
  opacity: number
  points: { x: number; y: number }[]
}

export type SketchText = {
  id: string
  x: number
  y: number
  text: string
  fontFamily: string
  fontSize: number
  fill: string
  fillOpacity: number
  bold: boolean
  italic: boolean
  underline: boolean
}

export type SketchArrow = {
  id: string
  from: { x: number; y: number }
  to: { x: number; y: number }
  color: string
  width: number
  opacity: number
  label?: string | null
  labelFontSize?: number
  labelBold?: boolean
  labelItalic?: boolean
  labelUnderline?: boolean
}

export type SketchShape = {
  id: string
  kind: ShapeKind
  x: number
  y: number
  w: number
  h: number
  color: string
  width: number
  opacity?: number
}

export const SKETCH_TEXT_LINE_HEIGHT = 1.2

export const SKETCH_FONTS = [
  { value: "system-ui, sans-serif", label: "System" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "ui-monospace, monospace", label: "Mono" },
  { value: "Arial, Helvetica, sans-serif", label: "Arial" },
] as const

export function createSketchText(partial?: Partial<SketchText>): SketchText {
  return {
    id: partial?.id ?? `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    x: partial?.x ?? 40,
    y: partial?.y ?? 40,
    text: partial?.text ?? "Text",
    fontFamily: partial?.fontFamily ?? SKETCH_FONTS[0].value,
    fontSize: partial?.fontSize ?? 28,
    fill: partial?.fill ?? "#111827",
    fillOpacity: partial?.fillOpacity ?? 1,
    bold: partial?.bold ?? true,
    italic: partial?.italic ?? false,
    underline: partial?.underline ?? false,
  }
}

type Props = {
  width: number
  height: number
  baseUrl?: string | null
  /** Preloaded CORS image; when set, skips network load for paint. */
  baseImage?: HTMLImageElement | null
  background?: "white" | "dark" | "none"
  strokes: SketchStroke[]
  texts: SketchText[]
  arrows: SketchArrow[]
  shapes: SketchShape[]
  tool: SketchTool
  color: string
  brushWidth: number
  brushOpacity: number
  eraserWidth: number
  arrowColor: string
  arrowWidth: number
  arrowLabelColor: string
  arrowLabelWidth: number
  shapeKind: ShapeKind
  shapeColor: string
  shapeWidth: number
  textColor: string
  penKind?: PenKind
  selectedIds?: string[]
  editingTextId?: string | null
  editingArrowId?: string | null
  disabled?: boolean
  className?: string
  deleteLabel: string
  textStyleLabels: { style: string; size: string; fill: string }
  onDeleteSelected: () => void
  onStrokesChange: (strokes: SketchStroke[]) => void
  onStrokeStart?: () => void
  onTextsChange: (texts: SketchText[]) => void
  onArrowsChange: (arrows: SketchArrow[]) => void
  onShapesChange: (shapes: SketchShape[]) => void
  onSelectIds: (ids: string[]) => void
  onEditingTextChange: (id: string | null) => void
  onEditingArrowChange: (id: string | null) => void
  onToolChange?: (tool: SketchTool) => void
  onObjectMutateStart?: () => void
  onTextColorChange?: (color: string) => void
  camera: SketchCamera
  onCameraChange: (camera: SketchCamera) => void
  onZoomPreset: (preset: SketchZoomPreset) => void
  /** When false: no zoom select, no wheel/pinch zoom or pan. Default true. */
  zoomEnabled?: boolean
  zoomOverlay: {
    value: string
    label: string
    fitLabel: string
    percent100Label: string
    percent200Label: string
    menuAria: string
  }
}

function withAlpha(color: string, alpha: number): string {
  if (alpha >= 1) return color
  if (color.startsWith("#") && (color.length === 7 || color.length === 4)) {
    const hex = color.length === 4 ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}` : color
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r},${g},${b},${alpha})`
  }
  return color
}

function fillPolygon(ctx: CanvasRenderingContext2D, poly: { x: number; y: number }[]) {
  if (poly.length < 3) return
  ctx.beginPath()
  poly.forEach((p, index) => {
    if (index === 0) ctx.moveTo(p.x, p.y)
    else ctx.lineTo(p.x, p.y)
  })
  ctx.closePath()
  ctx.fill()
}

function drawStroke(ctx: CanvasRenderingContext2D, stroke: SketchStroke) {
  if (stroke.tool === "eraser" || stroke.points.length === 0) return
  ctx.save()
  ctx.globalCompositeOperation = "source-over"
  ctx.globalAlpha = stroke.opacity ?? 1

  if (stroke.penKind === "highlighter") {
    ctx.fillStyle = stroke.color
    if (stroke.points.length === 1) {
      const p = stroke.points[0]
      const angle = pathTravelAngle(stroke.points)
      fillPolygon(ctx, orientedTipRect(p.x, p.y, angle, stroke.width, tipThicknessForWidth(stroke.width)))
    } else {
      const poly = strokeRibbonPolygon(stroke.points, stroke.width / 2)
      if (poly.length >= 3) fillPolygon(ctx, poly)
    }
    ctx.restore()
    return
  }

  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.lineWidth = stroke.width
  ctx.strokeStyle = stroke.color
  ctx.beginPath()
  stroke.points.forEach((point, index) => {
    if (index === 0) ctx.moveTo(point.x, point.y)
    else ctx.lineTo(point.x, point.y)
  })
  if (stroke.points.length === 1) {
    const p = stroke.points[0]
    ctx.arc(p.x, p.y, stroke.width / 2, 0, Math.PI * 2)
    ctx.fillStyle = stroke.color
    ctx.fill()
  } else {
    ctx.stroke()
  }
  ctx.restore()
}

function fontCss(t: SketchText): string {
  const style = t.italic ? "italic" : "normal"
  const weight = t.bold ? "700" : "400"
  return `${style} ${weight} ${t.fontSize}px ${t.fontFamily}`
}

export function drawSketchText(ctx: CanvasRenderingContext2D, t: SketchText) {
  const lines = (t.text || " ").split("\n")
  ctx.save()
  ctx.font = fontCss(t)
  ctx.textAlign = "left"
  ctx.textBaseline = "top"
  const linePx = t.fontSize * SKETCH_TEXT_LINE_HEIGHT
  lines.forEach((line, i) => {
    const y = t.y + i * linePx
    ctx.fillStyle = withAlpha(t.fill, t.fillOpacity)
    ctx.fillText(line, t.x, y)
    if (t.underline) {
      const metrics = ctx.measureText(line)
      const w = metrics.width
      ctx.strokeStyle = withAlpha(t.fill, t.fillOpacity)
      ctx.lineWidth = Math.max(1, t.fontSize / 16)
      const uy = y + t.fontSize * 0.92
      ctx.beginPath()
      ctx.moveTo(t.x, uy)
      ctx.lineTo(t.x + w, uy)
      ctx.stroke()
    }
  })
  ctx.restore()
}

export function drawSketchArrow(ctx: CanvasRenderingContext2D, a: SketchArrow) {
  const dx = a.to.x - a.from.x
  const dy = a.to.y - a.from.y
  const len = Math.hypot(dx, dy)
  if (len < 1) return
  const headLen = Math.max(10, a.width * 3.5)
  const ux = dx / len
  const uy = dy / len
  const tipX = a.to.x
  const tipY = a.to.y
  const baseX = tipX - ux * headLen
  const baseY = tipY - uy * headLen
  const px = -uy
  const py = ux
  const half = headLen * 0.45

  ctx.save()
  ctx.globalAlpha = a.opacity ?? 1
  ctx.strokeStyle = a.color
  ctx.fillStyle = a.color
  ctx.lineWidth = a.width
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.beginPath()
  ctx.moveTo(a.from.x, a.from.y)
  ctx.lineTo(baseX, baseY)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(tipX, tipY)
  ctx.lineTo(baseX + px * half, baseY + py * half)
  ctx.lineTo(baseX - px * half, baseY - py * half)
  ctx.closePath()
  ctx.fill()

  if (a.label != null) {
    const origin = arrowLabelOrigin(a)
    const fontSize = arrowLabelFontSize(a)
    const weight = a.labelBold ? "700" : "400"
    const style = a.labelItalic ? "italic" : "normal"
    ctx.font = `${style} ${weight} ${fontSize}px ${SKETCH_FONTS[0].value}`
    ctx.textAlign = "left"
    ctx.textBaseline = "top"
    ctx.fillStyle = withAlpha(a.color, a.opacity ?? 1)
    const label = a.label || " "
    ctx.fillText(label, origin.x, origin.y)
    if (a.labelUnderline) {
      const w = ctx.measureText(label).width
      ctx.strokeStyle = withAlpha(a.color, a.opacity ?? 1)
      ctx.lineWidth = Math.max(1, fontSize / 16)
      const uy = origin.y + fontSize * 0.92
      ctx.beginPath()
      ctx.moveTo(origin.x, uy)
      ctx.lineTo(origin.x + w, uy)
      ctx.stroke()
    }
  }
  ctx.restore()
}

export function drawSketchShape(ctx: CanvasRenderingContext2D, s: SketchShape) {
  if (s.w < 1 || s.h < 1) return
  ctx.save()
  ctx.globalAlpha = s.opacity ?? 1
  ctx.strokeStyle = s.color
  ctx.lineWidth = s.width
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.beginPath()
  if (s.kind === "ellipse") {
    ctx.ellipse(s.x + s.w / 2, s.y + s.h / 2, s.w / 2, s.h / 2, 0, 0, Math.PI * 2)
  } else {
    ctx.rect(s.x, s.y, s.w, s.h)
  }
  ctx.stroke()
  ctx.restore()
}

function syncCanvasDpr(canvas: HTMLCanvasElement, cssW: number, cssH: number): CanvasRenderingContext2D | null {
  const dpr = Math.max(1, window.devicePixelRatio || 1)
  const w = Math.max(1, Math.round(cssW))
  const h = Math.max(1, Math.round(cssH))
  const bw = Math.round(w * dpr)
  const bh = Math.round(h * dpr)
  if (canvas.width !== bw || canvas.height !== bh) {
    canvas.width = bw
    canvas.height = bh
  }
  canvas.style.width = `${w}px`
  canvas.style.height = `${h}px`
  const ctx = canvas.getContext("2d")
  if (!ctx) return null
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  return ctx
}

/** Optional letterbox fill + base image + pen strokes (object-eraser deletes objects, no paint punch). */
function drawPaint(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  background: "white" | "dark" | "none",
  img: HTMLImageElement | null,
  strokes: SketchStroke[],
  imageFit: "contain" | "stretch" = "contain",
) {
  ctx.clearRect(0, 0, width, height)
  ctx.globalCompositeOperation = "source-over"
  ctx.globalAlpha = 1
  if (img && imageFit === "stretch") {
    ctx.drawImage(img, 0, 0, width, height)
  } else {
    if (background !== "none") {
      ctx.fillStyle = background === "dark" ? "#1a1a1a" : "#ffffff"
      ctx.fillRect(0, 0, width, height)
    }
    if (img) drawContain(ctx, img, width, height)
  }
  strokes.forEach((stroke) => {
    if (stroke.tool === "pen") drawStroke(ctx, stroke)
  })
}

export async function flattenSketchToBlob(args: {
  width: number
  height: number
  baseUrl?: string | null
  baseImage?: HTMLImageElement | null
  background?: "white" | "dark" | "none"
  /** contain = letterbox (live preview); stretch = full-bleed export without white bars. */
  imageFit?: "contain" | "stretch"
  strokes: SketchStroke[]
  texts?: SketchText[]
  arrows?: SketchArrow[]
  shapes?: SketchShape[]
}): Promise<Blob> {
  const canvas = document.createElement("canvas")
  canvas.width = args.width
  canvas.height = args.height
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Canvas 2D unavailable")

  let img: HTMLImageElement | null = args.baseImage ?? null
  if (!img && args.baseUrl) {
    try {
      img = await loadSketchImage(args.baseUrl)
    } catch {
      img = null
    }
  }
  drawPaint(ctx, args.width, args.height, args.background ?? "white", img, args.strokes, args.imageFit ?? "contain")
  ;(args.shapes ?? []).forEach((s) => drawSketchShape(ctx, s))
  ;(args.arrows ?? []).forEach((a) => drawSketchArrow(ctx, a))
  ;(args.texts ?? []).forEach((t) => drawSketchText(ctx, t))

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"))
  if (!blob) throw new Error("PNG export failed")
  return blob
}

export function loadSketchImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error("Failed to load image"))
    img.src = url
  })
}

function drawContain(ctx: CanvasRenderingContext2D, img: HTMLImageElement, width: number, height: number) {
  const scale = Math.min(width / img.naturalWidth, height / img.naturalHeight)
  const w = img.naturalWidth * scale
  const h = img.naturalHeight * scale
  const x = (width - w) / 2
  const y = (height - h) / 2
  ctx.drawImage(img, x, y, w, h)
}

type SelectDrag =
  | { kind: "move"; lastX: number; lastY: number }
  | {
      kind: "resize"
      handle: ResizeHandle
      startBBox: BBox
      startPointer: { x: number; y: number }
      origins: {
        strokes: SketchStroke[]
        texts: SketchText[]
        arrows: SketchArrow[]
        shapes: SketchShape[]
      }
    }
  | { kind: "marquee"; x0: number; y0: number; x1: number; y1: number }

type ArrowDraft = { from: { x: number; y: number }; to: { x: number; y: number }; withLabel: boolean }

type ShapeDraft = { kind: ShapeKind; x0: number; y0: number; x1: number; y1: number }

function eventTargetInField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  const tag = el?.tagName?.toLowerCase()
  return tag === "input" || tag === "textarea" || Boolean(el?.isContentEditable)
}

function touchPairMidDist(
  points: Map<number, { x: number; y: number }>,
): { mid: { x: number; y: number }; dist: number } | null {
  if (points.size < 2) return null
  const [a, b] = [...points.values()]
  if (!a || !b) return null
  return {
    mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    dist: Math.hypot(b.x - a.x, b.y - a.y),
  }
}

export function SketchCanvas({
  width,
  height,
  baseUrl,
  baseImage,
  background = "white",
  strokes,
  texts,
  arrows,
  shapes,
  tool,
  color,
  brushWidth,
  brushOpacity,
  eraserWidth,
  arrowColor,
  arrowWidth,
  arrowLabelColor,
  arrowLabelWidth,
  shapeKind,
  shapeColor,
  shapeWidth,
  textColor,
  penKind,
  selectedIds = [],
  editingTextId = null,
  editingArrowId = null,
  disabled = false,
  className,
  deleteLabel,
  textStyleLabels,
  onDeleteSelected,
  onStrokesChange,
  onStrokeStart,
  onTextsChange,
  onArrowsChange,
  onShapesChange,
  onSelectIds,
  onEditingTextChange,
  onEditingArrowChange,
  onToolChange,
  onObjectMutateStart,
  onTextColorChange,
  camera,
  onCameraChange,
  onZoomPreset,
  zoomEnabled = true,
  zoomOverlay,
}: Props) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const paintRef = useRef<HTMLCanvasElement>(null)
  const overlayRef = useRef<HTMLCanvasElement>(null)
  const paintLoadGenRef = useRef(0)
  const editAreaRef = useRef<HTMLTextAreaElement>(null)
  const arrowEditRef = useRef<HTMLTextAreaElement>(null)
  const ignoreBlurRef = useRef(false)
  const justCommittedArrowIdRef = useRef<string | null>(null)
  const [drawing, setDrawing] = useState(false)
  const [erasing, setErasing] = useState(false)
  const [panning, setPanning] = useState(false)
  const [spaceHeld, setSpaceHeld] = useState(false)
  const [selectDrag, setSelectDrag] = useState<SelectDrag | null>(null)
  const [arrowDraft, setArrowDraft] = useState<ArrowDraft | null>(null)
  const [shapeDraft, setShapeDraft] = useState<ShapeDraft | null>(null)
  const [draggingTextId, setDraggingTextId] = useState<string | null>(null)
  const [brushCursor, setBrushCursor] = useState<{ x: number; y: number } | null>(null)
  const [brushAngle, setBrushAngle] = useState(Math.PI / 4)
  const lastBrushPointRef = useRef<{ x: number; y: number } | null>(null)
  const dragOffset = useRef({ x: 0, y: 0 })
  const lastSelectBodyDownAt = useRef(0)
  const panLastRef = useRef({ x: 0, y: 0 })
  const spaceHeldRef = useRef(false)
  const panningRef = useRef(false)
  const drawingRef = useRef(false)
  drawingRef.current = drawing
  const erasingRef = useRef(false)
  erasingRef.current = erasing
  const touchPointersRef = useRef(new Map<number, { x: number; y: number }>())
  const touchCameraRef = useRef(false)
  const skipTouchToolRef = useRef(false)
  const touchPanLastMidRef = useRef({ x: 0, y: 0 })
  const touchPinchLastDistRef = useRef(1)
  const cameraRef = useRef(camera)
  cameraRef.current = camera
  const onCameraChangeRef = useRef(onCameraChange)
  onCameraChangeRef.current = onCameraChange
  const strokesRef = useRef(strokes)
  strokesRef.current = strokes
  const textsRef = useRef(texts)
  textsRef.current = texts
  const arrowsRef = useRef(arrows)
  arrowsRef.current = arrows
  const shapesRef = useRef(shapes)
  shapesRef.current = shapes
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds
  const baseImgRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    if (tool !== "pen" && tool !== "eraser") setBrushCursor(null)
  }, [tool])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "Space" && e.key !== " ") return
      if (eventTargetInField(e.target)) return
      e.preventDefault()
      if (e.repeat || spaceHeldRef.current) return
      spaceHeldRef.current = true
      setSpaceHeld(true)
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== "Space" && e.key !== " ") return
      spaceHeldRef.current = false
      setSpaceHeld(false)
    }
    const onBlur = () => {
      spaceHeldRef.current = false
      setSpaceHeld(false)
    }
    window.addEventListener("keydown", onKeyDown, true)
    window.addEventListener("keyup", onKeyUp, true)
    window.addEventListener("blur", onBlur)
    return () => {
      window.removeEventListener("keydown", onKeyDown, true)
      window.removeEventListener("keyup", onKeyUp, true)
      window.removeEventListener("blur", onBlur)
    }
  }, [])

  useEffect(() => {
    const el = viewportRef.current
    if (!el || !zoomEnabled) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const current = cameraRef.current
      const next = zoomAt(
        current,
        { x: e.clientX - rect.left, y: e.clientY - rect.top },
        current.zoom * wheelZoomFactor(e.deltaY),
        width,
        height,
      )
      onCameraChangeRef.current(next)
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => {
      el.removeEventListener("wheel", onWheel)
    }
  }, [width, height, zoomEnabled])

  const editingTextIdRef = useRef(editingTextId)
  editingTextIdRef.current = editingTextId

  const finishEditingText = useCallback((): string | null => {
    const id = editingTextIdRef.current
    if (!id) {
      onEditingTextChange(null)
      return null
    }
    const current = textsRef.current.find((t) => t.id === id)
    if (!current?.text.trim()) {
      onTextsChange(textsRef.current.filter((t) => t.id !== id))
      onSelectIds(selectedIdsRef.current.filter((sid) => sid !== id))
      onEditingTextChange(null)
      return null
    }
    onEditingTextChange(null)
    return id
  }, [onEditingTextChange, onTextsChange, onSelectIds])

  const beginEditingText = useCallback(
    (id: string) => {
      ignoreBlurRef.current = true
      const prev = editingTextIdRef.current
      if (prev && prev !== id) {
        const current = textsRef.current.find((t) => t.id === prev)
        if (!current?.text.trim()) {
          onTextsChange(textsRef.current.filter((t) => t.id !== prev))
          onSelectIds(selectedIdsRef.current.filter((sid) => sid !== prev))
        }
      }
      onEditingArrowChange(null)
      onEditingTextChange(id)
      window.setTimeout(() => {
        ignoreBlurRef.current = false
        editAreaRef.current?.focus()
        editAreaRef.current?.select()
      }, 0)
    },
    [onEditingTextChange, onEditingArrowChange, onTextsChange, onSelectIds],
  )

  const beginEditingArrow = useCallback(
    (id: string) => {
      ignoreBlurRef.current = true
      finishEditingText()
      onEditingArrowChange(id)
      window.setTimeout(() => {
        ignoreBlurRef.current = false
        arrowEditRef.current?.focus()
        arrowEditRef.current?.select()
      }, 0)
    },
    [finishEditingText, onEditingArrowChange],
  )

  useEffect(() => {
    if (!editingTextId) return
    ignoreBlurRef.current = true
    const id = window.setTimeout(() => {
      ignoreBlurRef.current = false
      editAreaRef.current?.focus()
      editAreaRef.current?.select()
    }, 0)
    return () => window.clearTimeout(id)
  }, [editingTextId])

  useEffect(() => {
    if (!editingArrowId) return
    ignoreBlurRef.current = true
    const id = window.setTimeout(() => {
      ignoreBlurRef.current = false
      arrowEditRef.current?.focus()
      arrowEditRef.current?.select()
    }, 0)
    return () => window.clearTimeout(id)
  }, [editingArrowId])

  const redrawPaint = useCallback(() => {
    const canvas = paintRef.current
    if (!canvas) return
    const ctx = syncCanvasDpr(canvas, width, height)
    if (!ctx) return
    drawPaint(ctx, width, height, background, baseImgRef.current, strokesRef.current)
  }, [width, height, background])

  const reloadBaseAndPaint = useCallback(async () => {
    const canvas = paintRef.current
    if (!canvas) return
    const gen = ++paintLoadGenRef.current
    // Parent owns loading when baseImage is passed (including null while fetching).
    if (baseImage !== undefined) {
      baseImgRef.current = baseImage
      if (gen !== paintLoadGenRef.current) return
      redrawPaint()
      return
    }
    if (baseUrl) {
      try {
        const img = await loadSketchImage(baseUrl)
        if (paintRef.current !== canvas || gen !== paintLoadGenRef.current) return
        baseImgRef.current = img
      } catch {
        if (paintRef.current !== canvas || gen !== paintLoadGenRef.current) return
        baseImgRef.current = null
      }
    } else {
      baseImgRef.current = null
    }
    if (gen !== paintLoadGenRef.current) return
    redrawPaint()
  }, [baseImage, baseUrl, redrawPaint])

  const redrawOverlay = useCallback(() => {
    const canvas = overlayRef.current
    if (!canvas) return
    const ctx = syncCanvasDpr(canvas, width, height)
    if (!ctx) return
    ctx.clearRect(0, 0, width, height)
    shapesRef.current.forEach((s) => drawSketchShape(ctx, s))
    if (shapeDraft) {
      const box = normalizeRect(shapeDraft.x0, shapeDraft.y0, shapeDraft.x1, shapeDraft.y1)
      drawSketchShape(ctx, {
        id: "draft",
        kind: shapeDraft.kind,
        x: box.x,
        y: box.y,
        w: box.w,
        h: box.h,
        color: shapeColor,
        width: shapeWidth,
        opacity: 1,
      })
    }
    arrowsRef.current.forEach((a) => {
      if (a.id === editingArrowId) {
        drawSketchArrow(ctx, { ...a, label: null })
      } else {
        drawSketchArrow(ctx, a)
      }
    })
    if (arrowDraft) {
      drawSketchArrow(ctx, {
        id: "draft",
        from: arrowDraft.from,
        to: arrowDraft.to,
        color: arrowDraft.withLabel ? arrowLabelColor : arrowColor,
        width: arrowDraft.withLabel ? arrowLabelWidth : arrowWidth,
        opacity: 1,
        label: arrowDraft.withLabel ? "" : null,
      })
    }
  }, [
    width,
    height,
    editingArrowId,
    arrowDraft,
    shapeDraft,
    arrowColor,
    arrowWidth,
    arrowLabelColor,
    arrowLabelWidth,
    shapeColor,
    shapeWidth,
  ])

  useEffect(() => {
    void reloadBaseAndPaint()
  }, [reloadBaseAndPaint])

  useEffect(() => {
    redrawPaint()
  }, [redrawPaint, strokes])

  useEffect(() => {
    redrawOverlay()
  }, [redrawOverlay, arrows, shapes])

  const pointerPos = (e: React.PointerEvent) => {
    const canvas = paintRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    const scaleX = width / rect.width
    const scaleY = height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }

  const objectBBox = useCallback(
    (id: string): BBox | null => {
      const stroke = strokes.find((s) => s.id === id && s.tool === "pen")
      if (stroke) return strokeBBox(stroke)
      const text = texts.find((t) => t.id === id)
      if (text) return textBBox(text)
      const arrow = arrows.find((a) => a.id === id)
      if (arrow) return arrowBBox(arrow)
      const shape = shapes.find((s) => s.id === id)
      if (shape) return shapeBBox(shape)
      return null
    },
    [strokes, texts, arrows, shapes],
  )

  const selectionBBox = useMemo(() => {
    const boxes = selectedIds.map((id) => objectBBox(id)).filter((b): b is BBox => b != null)
    return unionBBox(boxes)
  }, [selectedIds, objectBBox])

  const selectedFrames = useMemo(() => {
    return selectedIds
      .map((id) => {
        const box = objectBBox(id)
        return box ? { id, box } : null
      })
      .filter((item): item is { id: string; box: BBox } => item != null)
  }, [selectedIds, objectBBox])

  const marqueeRect =
    selectDrag?.kind === "marquee" ? normalizeRect(selectDrag.x0, selectDrag.y0, selectDrag.x1, selectDrag.y1) : null

  const marqueePreviewFrames = useMemo(() => {
    if (!marqueeRect || (marqueeRect.w < 2 && marqueeRect.h < 2)) return [] as { id: string; box: BBox }[]
    const frames: { id: string; box: BBox }[] = []
    for (const s of strokes) {
      if (s.tool === "eraser") continue
      const box = strokeBBox(s)
      if (bboxIntersects(marqueeRect, box)) frames.push({ id: s.id, box })
    }
    for (const t of texts) {
      const box = textBBox(t)
      if (bboxIntersects(marqueeRect, box)) frames.push({ id: t.id, box })
    }
    for (const a of arrows) {
      const box = arrowBBox(a)
      if (bboxIntersects(marqueeRect, box)) frames.push({ id: a.id, box })
    }
    for (const s of shapes) {
      const box = shapeBBox(s)
      if (bboxIntersects(marqueeRect, box)) frames.push({ id: s.id, box })
    }
    return frames
  }, [marqueeRect, strokes, texts, arrows, shapes])

  const singleSelectedId = selectedIds.length === 1 ? selectedIds[0] : null
  const selectedText = singleSelectedId ? (texts.find((t) => t.id === singleSelectedId) ?? null) : null
  const selectedArrow = singleSelectedId ? (arrows.find((a) => a.id === singleSelectedId) ?? null) : null

  const moveSelectedBy = (dx: number, dy: number) => {
    const ids = new Set(selectedIdsRef.current)
    if (ids.size === 0) return
    onStrokesChange(strokesRef.current.map((s) => (ids.has(s.id) ? translateStroke(s, dx, dy) : s)))
    onTextsChange(textsRef.current.map((t) => (ids.has(t.id) ? translateText(t, dx, dy) : t)))
    onArrowsChange(arrowsRef.current.map((a) => (ids.has(a.id) ? translateArrow(a, dx, dy) : a)))
    onShapesChange(shapesRef.current.map((s) => (ids.has(s.id) ? translateShape(s, dx, dy) : s)))
  }

  const eraseInkAt = useCallback(
    (x: number, y: number) => {
      const radius = eraserWidth / 2
      const prev = strokesRef.current
      const next = eraseStrokesAt(prev, x, y, radius, newStrokeId)
      if (next === prev) return
      if (next.length === prev.length && next.every((s, i) => s === prev[i])) return
      const nextIds = new Set(next.map((s) => s.id))
      const removedSelected = selectedIdsRef.current.filter((id) => {
        const wasStroke = prev.some((s) => s.id === id)
        return wasStroke && !nextIds.has(id)
      })
      onStrokesChange(next)
      if (removedSelected.length > 0) {
        const drop = new Set(removedSelected)
        onSelectIds(selectedIdsRef.current.filter((id) => !drop.has(id)))
      }
    },
    [eraserWidth, onStrokesChange, onSelectIds],
  )

  const abortToolGesture = () => {
    if (drawingRef.current) {
      const next = [...strokesRef.current]
      const last = next[next.length - 1]
      if (last?.tool === "pen") next.pop()
      onStrokesChange(next)
      drawingRef.current = false
      setDrawing(false)
    }
    if (erasingRef.current) {
      erasingRef.current = false
      setErasing(false)
    }
    setSelectDrag(null)
    setArrowDraft(null)
    setShapeDraft(null)
    setDraggingTextId(null)
  }

  const beginTouchCamera = (el: HTMLDivElement) => {
    abortToolGesture()
    skipTouchToolRef.current = true
    touchCameraRef.current = true
    panningRef.current = true
    setPanning(true)
    const pair = touchPairMidDist(touchPointersRef.current)
    if (pair) {
      touchPanLastMidRef.current = pair.mid
      touchPinchLastDistRef.current = pair.dist > 0 ? pair.dist : 1
    }
    for (const id of touchPointersRef.current.keys()) {
      try {
        el.setPointerCapture(id)
      } catch {
        // not yet down on this element
      }
    }
  }

  const applyTouchCameraMove = (viewport: HTMLDivElement) => {
    if (!zoomEnabled) return
    const pair = touchPairMidDist(touchPointersRef.current)
    if (!pair) return
    const dx = pair.mid.x - touchPanLastMidRef.current.x
    const dy = pair.mid.y - touchPanLastMidRef.current.y
    const prevDist = touchPinchLastDistRef.current
    touchPanLastMidRef.current = pair.mid
    touchPinchLastDistRef.current = pair.dist > 0 ? pair.dist : prevDist
    let next = cameraRef.current
    next = clampPan({ ...next, panX: next.panX + dx, panY: next.panY + dy }, width, height)
    if (prevDist > 0 && pair.dist > 0) {
      const rect = viewport.getBoundingClientRect()
      next = zoomAt(
        next,
        { x: pair.mid.x - rect.left, y: pair.mid.y - rect.top },
        next.zoom * (pair.dist / prevDist),
        width,
        height,
      )
    }
    onCameraChange(next)
  }

  const onPointerDownCapture = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "touch") return
    if (!zoomEnabled) return
    if ((e.target as HTMLElement | null)?.closest?.("[data-sketch-zoom]")) return
    if ((e.target as HTMLElement | null)?.closest?.("textarea")) return
    touchPointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (touchPointersRef.current.size === 2) {
      e.preventDefault()
      beginTouchCamera(e.currentTarget)
    }
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "touch" && (touchCameraRef.current || skipTouchToolRef.current)) {
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      return
    }
    if ((e.target as HTMLElement | null)?.closest?.("textarea")) return
    if ((e.target as HTMLElement | null)?.closest?.("[data-sketch-selection]")) return
    if ((e.target as HTMLElement | null)?.closest?.("[data-sketch-text-style]")) return
    if ((e.target as HTMLElement | null)?.closest?.("[data-sketch-zoom]")) return

    const wantsPan = e.button === 1 || (e.button === 0 && (spaceHeldRef.current || tool === "pan"))
    if (wantsPan) {
      e.preventDefault()
      panningRef.current = true
      setPanning(true)
      panLastRef.current = { x: e.clientX, y: e.clientY }
      e.currentTarget.setPointerCapture(e.pointerId)
      return
    }

    if (disabled) return

    const point = pointerPos(e)

    const labeledArrowAt = (x: number, y: number): SketchArrow | null => {
      const onLabel = hitTestArrowLabel(arrowsRef.current, x, y)
      if (onLabel) return onLabel
      // Empty label box is tiny — allow shaft hit when arrow already has a label field.
      const a = hitTestArrow(arrowsRef.current, x, y)
      return a && a.label != null ? a : null
    }

    if (tool === "select") {
      const keepArrowId = justCommittedArrowIdRef.current
      justCommittedArrowIdRef.current = null

      const hitArrow = hitTestArrow(arrows, point.x, point.y)
      const hitShape = hitArrow ? null : hitTestShape(shapes, point.x, point.y)
      const hitText = hitArrow || hitShape ? null : hitTestText(texts, point.x, point.y)
      const hitStroke = hitArrow || hitShape || hitText ? null : hitTestStroke(strokes, point.x, point.y)
      const hitId = hitArrow?.id ?? hitShape?.id ?? hitText?.id ?? hitStroke?.id ?? null

      if (hitText && e.detail >= 2) {
        onEditingArrowChange(null)
        onSelectIds([hitText.id])
        beginEditingText(hitText.id)
        return
      }

      if (hitArrow && hitArrow.label != null && e.detail >= 2) {
        finishEditingText()
        onSelectIds([hitArrow.id])
        beginEditingArrow(hitArrow.id)
        return
      }

      finishEditingText()
      onEditingArrowChange(null)

      if (hitId) {
        if (e.shiftKey) {
          const next = selectedIds.includes(hitId) ? selectedIds.filter((id) => id !== hitId) : [...selectedIds, hitId]
          onSelectIds(next)
          return
        }
        const nextIds = selectedIds.includes(hitId) && selectedIds.length > 1 ? selectedIds : [hitId]
        onSelectIds(nextIds)
        onObjectMutateStart?.()
        setSelectDrag({ kind: "move", lastX: point.x, lastY: point.y })
        e.currentTarget.setPointerCapture(e.pointerId)
        return
      }

      if (selectionBBox && pointInBBox(selectionBBox, point.x, point.y)) {
        onObjectMutateStart?.()
        setSelectDrag({ kind: "move", lastX: point.x, lastY: point.y })
        e.currentTarget.setPointerCapture(e.pointerId)
        return
      }

      // Click that dismissed the arrow-label editor: keep that arrow selected (no empty marquee clear).
      if (keepArrowId) {
        onSelectIds([keepArrowId])
        return
      }

      if (e.detail >= 2) {
        onCameraChange(fitCamera())
        return
      }

      setSelectDrag({ kind: "marquee", x0: point.x, y0: point.y, x1: point.x, y1: point.y })
      e.currentTarget.setPointerCapture(e.pointerId)
      return
    }

    if (tool === "text") {
      // Click outside the active editor: commit text and switch to select (no new text).
      if (editingTextId) {
        const committedId = finishEditingText()
        onToolChange?.("select")
        const hit = hitTestText(textsRef.current, point.x, point.y)
        if (hit) onSelectIds([hit.id])
        else if (committedId) onSelectIds([committedId])
        else onSelectIds([])
        return
      }
      if (editingArrowId) {
        onEditingArrowChange(null)
      }

      const hitLabeledArrow = labeledArrowAt(point.x, point.y)
      if (hitLabeledArrow) {
        onSelectIds([hitLabeledArrow.id])
        beginEditingArrow(hitLabeledArrow.id)
        return
      }

      const hit = hitTestText(texts, point.x, point.y)
      if (hit) {
        onSelectIds([hit.id])
        if (hit.id === singleSelectedId || e.detail >= 2) {
          beginEditingText(hit.id)
          return
        }
        finishEditingText()
        onObjectMutateStart?.()
        setDraggingTextId(hit.id)
        dragOffset.current = { x: point.x - hit.x, y: point.y - hit.y }
        e.currentTarget.setPointerCapture(e.pointerId)
        return
      }
      onObjectMutateStart?.()
      const created = createSketchText({ x: point.x, y: point.y, fill: textColor, text: "" })
      onTextsChange([...texts, created])
      onSelectIds([created.id])
      beginEditingText(created.id)
      return
    }

    if (tool === "arrowLabel") {
      if (editingArrowId) {
        onEditingArrowChange(null)
      }
      finishEditingText()
      const hitLabeledArrow = labeledArrowAt(point.x, point.y)
      if (hitLabeledArrow) {
        onSelectIds([hitLabeledArrow.id])
        beginEditingArrow(hitLabeledArrow.id)
        return
      }
      onSelectIds([])
      e.currentTarget.setPointerCapture(e.pointerId)
      setArrowDraft({ from: point, to: point, withLabel: true })
      return
    }

    if (tool === "arrow") {
      onSelectIds([])
      finishEditingText()
      onEditingArrowChange(null)
      e.currentTarget.setPointerCapture(e.pointerId)
      setArrowDraft({ from: point, to: point, withLabel: false })
      return
    }

    if (tool === "shape") {
      onSelectIds([])
      finishEditingText()
      onEditingArrowChange(null)
      e.currentTarget.setPointerCapture(e.pointerId)
      setShapeDraft({ kind: shapeKind, x0: point.x, y0: point.y, x1: point.x, y1: point.y })
      return
    }

    if (tool === "eraser") {
      e.preventDefault()
      window.getSelection()?.removeAllRanges()
      onSelectIds([])
      finishEditingText()
      onEditingArrowChange(null)
      e.currentTarget.setPointerCapture(e.pointerId)
      setErasing(true)
      onStrokeStart?.()
      eraseInkAt(point.x, point.y)
      return
    }

    if (tool === "pen") {
      e.preventDefault()
      window.getSelection()?.removeAllRanges()
      onSelectIds([])
      finishEditingText()
      onEditingArrowChange(null)
      e.currentTarget.setPointerCapture(e.pointerId)
      setDrawing(true)
      onStrokeStart?.()
      onStrokesChange([
        ...strokes,
        {
          id: newStrokeId(),
          tool: "pen",
          penKind,
          color,
          width: brushWidth,
          opacity: brushOpacity,
          points: [point],
        },
      ])
    }
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "touch" && touchPointersRef.current.has(e.pointerId)) {
      touchPointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    }
    if (touchCameraRef.current && touchPointersRef.current.size >= 2) {
      applyTouchCameraMove(e.currentTarget)
      return
    }
    if (e.pointerType === "touch" && skipTouchToolRef.current) return

    if (panningRef.current) {
      const dx = e.clientX - panLastRef.current.x
      const dy = e.clientY - panLastRef.current.y
      panLastRef.current = { x: e.clientX, y: e.clientY }
      const current = cameraRef.current
      onCameraChange(clampPan({ ...current, panX: current.panX + dx, panY: current.panY + dy }, width, height))
      return
    }

    if (disabled) return
    const point = pointerPos(e)

    if (tool === "pen" || tool === "eraser") {
      setBrushCursor(point)
      if (tool === "pen") {
        const prev = lastBrushPointRef.current
        if (prev) {
          const dx = point.x - prev.x
          const dy = point.y - prev.y
          if (Math.hypot(dx, dy) >= 2) setBrushAngle(Math.atan2(dy, dx))
        }
        lastBrushPointRef.current = point
      }
    } else if (brushCursor) {
      setBrushCursor(null)
      lastBrushPointRef.current = null
    }

    if (arrowDraft) {
      setArrowDraft({ ...arrowDraft, to: point })
      return
    }

    if (shapeDraft) {
      setShapeDraft({ ...shapeDraft, x1: point.x, y1: point.y })
      return
    }

    if (selectDrag) {
      if (selectDrag.kind === "marquee") {
        setSelectDrag({ ...selectDrag, x1: point.x, y1: point.y })
        return
      }

      if (selectDrag.kind === "move") {
        const dx = point.x - selectDrag.lastX
        const dy = point.y - selectDrag.lastY
        setSelectDrag({ kind: "move", lastX: point.x, lastY: point.y })
        moveSelectedBy(dx, dy)
        return
      }

      const dx = point.x - selectDrag.startPointer.x
      const dy = point.y - selectDrag.startPointer.y
      const nextBox = resizeBBox(selectDrag.startBBox, selectDrag.handle, dx, dy)
      const {
        strokes: originStrokes,
        texts: originTexts,
        arrows: originArrows,
        shapes: originShapes,
      } = selectDrag.origins
      const start = selectDrag.startBBox

      if (originStrokes.length > 0) {
        const byId = new Map(originStrokes.map((o) => [o.id, o]))
        onStrokesChange(
          strokesRef.current.map((s) => {
            const origin = byId.get(s.id)
            return origin ? transformStrokeToBBox(origin, start, nextBox) : s
          }),
        )
      }
      if (originTexts.length > 0) {
        const byId = new Map(originTexts.map((o) => [o.id, o]))
        onTextsChange(
          textsRef.current.map((t) => {
            const origin = byId.get(t.id)
            return origin ? transformTextToBBox(origin, start, nextBox) : t
          }),
        )
      }
      if (originArrows.length > 0) {
        const byId = new Map(originArrows.map((o) => [o.id, o]))
        onArrowsChange(
          arrowsRef.current.map((a) => {
            const origin = byId.get(a.id)
            return origin ? transformArrowToBBox(origin, start, nextBox) : a
          }),
        )
      }
      if (originShapes.length > 0) {
        const byId = new Map(originShapes.map((o) => [o.id, o]))
        onShapesChange(
          shapesRef.current.map((s) => {
            const origin = byId.get(s.id)
            return origin ? transformShapeToBBox(origin, start, nextBox) : s
          }),
        )
      }
      return
    }

    if (draggingTextId) {
      onTextsChange(
        texts.map((t) =>
          t.id === draggingTextId ? { ...t, x: point.x - dragOffset.current.x, y: point.y - dragOffset.current.y } : t,
        ),
      )
      return
    }

    if (erasing) {
      eraseInkAt(point.x, point.y)
      return
    }

    if (!drawing) return
    const next = [...strokes]
    const last = next[next.length - 1]
    if (!last || last.tool !== "pen") return
    next[next.length - 1] = { ...last, points: [...last.points, point] }
    onStrokesChange(next)
  }

  const endPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "touch") {
      touchPointersRef.current.delete(e.pointerId)
      const skipTool = skipTouchToolRef.current || touchCameraRef.current
      if (touchPointersRef.current.size < 2 && touchCameraRef.current) {
        touchCameraRef.current = false
        panningRef.current = false
        setPanning(false)
      }
      if (touchPointersRef.current.size === 0) skipTouchToolRef.current = false
      if (skipTool) {
        try {
          e.currentTarget.releasePointerCapture(e.pointerId)
        } catch {
          // already released
        }
        return
      }
    }

    if (arrowDraft) {
      const len = Math.hypot(arrowDraft.to.x - arrowDraft.from.x, arrowDraft.to.y - arrowDraft.from.y)
      if (len >= 8) {
        onObjectMutateStart?.()
        const created: SketchArrow = {
          id: newArrowId(),
          from: arrowDraft.from,
          to: arrowDraft.to,
          color: arrowDraft.withLabel ? arrowLabelColor : arrowColor,
          width: arrowDraft.withLabel ? arrowLabelWidth : arrowWidth,
          opacity: 1,
          label: arrowDraft.withLabel ? "" : null,
          labelFontSize: 16,
          labelBold: arrowDraft.withLabel ? true : undefined,
        }
        onArrowsChange([...arrowsRef.current, created])
        onSelectIds([created.id])
        if (arrowDraft.withLabel) beginEditingArrow(created.id)
        onToolChange?.("select")
      }
      setArrowDraft(null)
    }

    if (shapeDraft) {
      const box = normalizeRect(shapeDraft.x0, shapeDraft.y0, shapeDraft.x1, shapeDraft.y1)
      if (box.w >= 4 && box.h >= 4) {
        onObjectMutateStart?.()
        const created: SketchShape = {
          id: newShapeId(),
          kind: shapeDraft.kind,
          x: box.x,
          y: box.y,
          w: box.w,
          h: box.h,
          color: shapeColor,
          width: shapeWidth,
          opacity: 1,
        }
        onShapesChange([...shapesRef.current, created])
        onSelectIds([created.id])
        onToolChange?.("select")
      }
      setShapeDraft(null)
    }

    if (selectDrag?.kind === "marquee") {
      const rect = normalizeRect(selectDrag.x0, selectDrag.y0, selectDrag.x1, selectDrag.y1)
      if (rect.w < 4 && rect.h < 4) {
        onSelectIds([])
      } else {
        const ids: string[] = []
        for (const s of strokesRef.current) {
          if (s.tool === "eraser") continue
          if (bboxIntersects(rect, strokeBBox(s))) ids.push(s.id)
        }
        for (const t of textsRef.current) {
          if (bboxIntersects(rect, textBBox(t))) ids.push(t.id)
        }
        for (const a of arrowsRef.current) {
          if (bboxIntersects(rect, arrowBBox(a))) ids.push(a.id)
        }
        for (const s of shapesRef.current) {
          if (bboxIntersects(rect, shapeBBox(s))) ids.push(s.id)
        }
        onSelectIds(ids)
      }
    }

    if (drawing) {
      const next = [...strokesRef.current]
      const last = next[next.length - 1]
      if (last?.tool === "pen") {
        next[next.length - 1] = {
          ...last,
          points: finalizePenStrokePoints(last.points, last.penKind ?? "marker"),
        }
        onStrokesChange(next)
      }
      setDrawing(false)
    }
    if (erasing) setErasing(false)
    if (draggingTextId) setDraggingTextId(null)
    if (selectDrag) setSelectDrag(null)
    if (panningRef.current) {
      panningRef.current = false
      setPanning(false)
    }
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      // already released
    }
  }

  const startResize = (handle: ResizeHandle, e: React.PointerEvent) => {
    if (disabled || tool !== "select" || !selectionBBox || selectedIdsRef.current.length === 0) return
    e.preventDefault()
    e.stopPropagation()
    onObjectMutateStart?.()
    const point = pointerPos(e)
    const ids = new Set(selectedIdsRef.current)
    setSelectDrag({
      kind: "resize",
      handle,
      startBBox: selectionBBox,
      startPointer: point,
      origins: {
        strokes: strokesRef.current
          .filter((s) => ids.has(s.id) && s.tool === "pen")
          .map((s) => ({ ...s, points: s.points.map((p) => ({ ...p })) })),
        texts: textsRef.current.filter((t) => ids.has(t.id)).map((t) => ({ ...t })),
        arrows: arrowsRef.current
          .filter((a) => ids.has(a.id))
          .map((a) => ({ ...a, from: { ...a.from }, to: { ...a.to } })),
        shapes: shapesRef.current.filter((s) => ids.has(s.id)).map((s) => ({ ...s })),
      },
    })
    ;(e.currentTarget.closest("[data-sketch-root]") as HTMLElement | null)?.setPointerCapture?.(e.pointerId)
  }

  const startBodyMove = (e: React.PointerEvent) => {
    if (disabled || tool !== "select" || selectedIds.length === 0) return
    e.preventDefault()
    e.stopPropagation()
    // Overlay preventDefault can reset click detail; also accept a fast second pointerdown.
    const now = performance.now()
    const isDouble = e.detail >= 2 || now - lastSelectBodyDownAt.current < 350
    lastSelectBodyDownAt.current = now
    if (isDouble && selectedText) {
      beginEditingText(selectedText.id)
      return
    }
    if (isDouble && selectedArrow && selectedArrow.label != null) {
      beginEditingArrow(selectedArrow.id)
      return
    }
    onObjectMutateStart?.()
    const point = pointerPos(e)
    setSelectDrag({ kind: "move", lastX: point.x, lastY: point.y })
    ;(e.currentTarget.closest("[data-sketch-root]") as HTMLElement | null)?.setPointerCapture?.(e.pointerId)
  }

  const cursor =
    disabled && !spaceHeld && !panning && tool !== "pan"
      ? "cursor-not-allowed"
      : panning
        ? "cursor-grabbing"
        : spaceHeld || tool === "pan"
          ? "cursor-grab"
          : tool === "text"
            ? "cursor-text"
            : tool === "select"
              ? "cursor-default"
              : tool === "pen" || tool === "eraser"
                ? "cursor-none"
                : "cursor-crosshair"

  const editingArrow = editingArrowId ? arrows.find((a) => a.id === editingArrowId) : null
  const editingArrowOrigin = editingArrow ? arrowLabelOrigin(editingArrow) : null
  const editingArrowMetrics =
    editingArrow && editingArrowOrigin
      ? textBBox({
          x: editingArrowOrigin.x,
          y: editingArrowOrigin.y,
          text: editingArrow.label || " ",
          fontSize: arrowLabelFontSize(editingArrow),
          fontFamily: SKETCH_FONTS[0].value,
          bold: editingArrow.labelBold,
          italic: editingArrow.labelItalic,
        })
      : null

  const styleTargetText = editingTextId
    ? (texts.find((t) => t.id === editingTextId) ?? null)
    : selectedIds.length === 1
      ? selectedText
      : null
  const styleTargetArrow = styleTargetText
    ? null
    : editingArrowId
      ? (arrows.find((a) => a.id === editingArrowId && a.label != null) ?? null)
      : selectedIds.length === 1 && selectedArrow?.label != null
        ? selectedArrow
        : null
  const styleAnchor = styleTargetText
    ? textBBox(styleTargetText)
    : styleTargetArrow
      ? (() => {
          const origin = arrowLabelOrigin(styleTargetArrow)
          return textBBox({
            x: origin.x,
            y: origin.y,
            text: styleTargetArrow.label || " ",
            fontSize: arrowLabelFontSize(styleTargetArrow),
            fontFamily: SKETCH_FONTS[0].value,
            bold: styleTargetArrow.labelBold,
            italic: styleTargetArrow.labelItalic,
          })
        })()
      : null
  const styleValue = styleTargetText
    ? {
        fontSize: styleTargetText.fontSize,
        fill: styleTargetText.fill,
        bold: styleTargetText.bold,
        italic: styleTargetText.italic,
        underline: styleTargetText.underline,
      }
    : styleTargetArrow
      ? {
          fontSize: arrowLabelFontSize(styleTargetArrow),
          fill: styleTargetArrow.color,
          bold: Boolean(styleTargetArrow.labelBold),
          italic: Boolean(styleTargetArrow.labelItalic),
          underline: Boolean(styleTargetArrow.labelUnderline),
        }
      : null

  return (
    <div
      ref={viewportRef}
      data-sketch-root=""
      className={cn("absolute inset-0 min-h-0 min-w-0 touch-none overflow-hidden select-none", className, cursor)}
      onPointerDownCapture={onPointerDownCapture}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onPointerLeave={() => setBrushCursor(null)}
      onMouseDown={(e) => {
        if (e.button === 1) e.preventDefault()
      }}
    >
      <div
        className="absolute top-0 left-0"
        style={{
          width,
          height,
          transform: `translate(${camera.panX}px, ${camera.panY}px) scale(${camera.zoom})`,
          transformOrigin: "0 0",
          contain: "strict",
        }}
      >
        <canvas ref={paintRef} className={cn("pointer-events-none absolute inset-0 block", disabled && "opacity-70")} />
        <canvas ref={overlayRef} className="pointer-events-none absolute inset-0 block" />
        {texts.map((t) => {
          const selected = selectedIds.includes(t.id)
          const editing = t.id === editingTextId
          const metrics = textBBox(t)
          const boxStyle: CSSProperties = {
            left: t.x,
            top: t.y,
            width: metrics.w,
            height: metrics.h,
            maxWidth: metrics.w,
            maxHeight: metrics.h,
            boxSizing: "border-box",
            fontFamily: t.fontFamily,
            fontSize: t.fontSize,
            fontWeight: t.bold ? 700 : 400,
            fontStyle: t.italic ? "italic" : "normal",
            textDecoration: t.underline ? "underline" : "none",
            color: withAlpha(t.fill, t.fillOpacity),
            lineHeight: SKETCH_TEXT_LINE_HEIGHT,
            textAlign: "left",
            pointerEvents: editing || tool === "text" ? "auto" : "none",
            userSelect: editing || tool === "text" ? "auto" : "none",
          }
          if (editing) {
            return (
              <textarea
                key={t.id}
                ref={editAreaRef}
                rows={Math.max(1, (t.text || "").split("\n").length)}
                className="absolute z-20 resize-none overflow-hidden border-0 bg-transparent p-0 shadow-none ring-0 outline-none"
                style={boxStyle}
                value={t.text}
                onPointerDown={(ev) => ev.stopPropagation()}
                onKeyDown={(ev) => {
                  ev.stopPropagation()
                  if (ev.key === "Escape" || (ev.key === "Enter" && !ev.shiftKey)) {
                    ev.preventDefault()
                    const id = finishEditingText()
                    onToolChange?.("select")
                    if (id) onSelectIds([id])
                  }
                }}
                onChange={(ev) => {
                  onTextsChange(texts.map((item) => (item.id === t.id ? { ...item, text: ev.target.value } : item)))
                }}
                onBlur={() => {
                  if (ignoreBlurRef.current) return
                  finishEditingText()
                }}
              />
            )
          }
          return (
            <div
              key={t.id}
              className={cn(
                "absolute z-10 overflow-hidden whitespace-pre select-none",
                selected && tool === "text" && "border border-ui-accent",
              )}
              style={{
                ...boxStyle,
                ...(selected && tool === "text" ? { borderWidth: hairlinePx(camera.zoom) } : {}),
              }}
              onPointerDown={(ev) => {
                if (tool !== "text" || disabled) return
                ev.stopPropagation()
                if (editingTextId) {
                  finishEditingText()
                  onToolChange?.("select")
                  onSelectIds([t.id])
                  return
                }
                onSelectIds([t.id])
                if (t.id === singleSelectedId || ev.detail >= 2) {
                  beginEditingText(t.id)
                  return
                }
                onObjectMutateStart?.()
                setDraggingTextId(t.id)
                const p = pointerPos(ev)
                dragOffset.current = { x: p.x - t.x, y: p.y - t.y }
                ;(ev.currentTarget.parentElement as HTMLElement | null)?.setPointerCapture?.(ev.pointerId)
              }}
            >
              {t.text || " "}
            </div>
          )
        })}
        {editingArrow && editingArrowOrigin && editingArrowMetrics ? (
          <textarea
            ref={arrowEditRef}
            rows={1}
            className="absolute z-20 resize-none overflow-hidden border-0 bg-transparent p-0 shadow-none ring-0 outline-none"
            style={{
              left: editingArrowOrigin.x,
              top: editingArrowOrigin.y,
              width: editingArrowMetrics.w,
              height: editingArrowMetrics.h,
              maxWidth: editingArrowMetrics.w,
              maxHeight: editingArrowMetrics.h,
              boxSizing: "border-box",
              fontFamily: SKETCH_FONTS[0].value,
              fontSize: arrowLabelFontSize(editingArrow),
              fontWeight: editingArrow.labelBold ? 700 : 400,
              fontStyle: editingArrow.labelItalic ? "italic" : "normal",
              textDecoration: editingArrow.labelUnderline ? "underline" : "none",
              lineHeight: SKETCH_TEXT_LINE_HEIGHT,
              color: withAlpha(editingArrow.color, editingArrow.opacity),
            }}
            value={editingArrow.label ?? ""}
            onPointerDown={(ev) => ev.stopPropagation()}
            onKeyDown={(ev) => {
              ev.stopPropagation()
              if (ev.key === "Escape" || (ev.key === "Enter" && !ev.shiftKey)) {
                ev.preventDefault()
                onSelectIds([editingArrow.id])
                onEditingArrowChange(null)
              }
            }}
            onChange={(ev) => {
              onArrowsChange(
                arrows.map((item) => (item.id === editingArrow.id ? { ...item, label: ev.target.value } : item)),
              )
            }}
            onBlur={() => {
              if (ignoreBlurRef.current) return
              justCommittedArrowIdRef.current = editingArrow.id
              onSelectIds([editingArrow.id])
              onEditingArrowChange(null)
            }}
          />
        ) : null}
        {marqueeRect && marqueeRect.w + marqueeRect.h > 0 ? (
          <div
            className="pointer-events-none absolute z-20 border border-ui-accent bg-ui-accent/10"
            style={{
              left: marqueeRect.x,
              top: marqueeRect.y,
              width: marqueeRect.w,
              height: marqueeRect.h,
              borderWidth: hairlinePx(camera.zoom),
            }}
          />
        ) : null}
        {tool === "select" && marqueePreviewFrames.length > 0
          ? marqueePreviewFrames.map(({ id, box }) => (
              <div
                key={`marquee-${id}`}
                className="pointer-events-none absolute z-[25] border border-ui-accent"
                style={{
                  left: box.x,
                  top: box.y,
                  width: box.w,
                  height: box.h,
                  borderWidth: hairlinePx(camera.zoom),
                }}
              />
            ))
          : null}
        {tool === "select" && selectedFrames.length > 0 && !editingTextId && !editingArrowId ? (
          <div data-sketch-selection="">
            {selectedFrames.length > 1
              ? selectedFrames.map(({ id, box }) => (
                  <div
                    key={id}
                    className="pointer-events-auto absolute z-30 border border-ui-accent"
                    style={{
                      left: box.x,
                      top: box.y,
                      width: box.w,
                      height: box.h,
                      cursor: "move",
                      borderWidth: hairlinePx(camera.zoom),
                    }}
                    onPointerDown={startBodyMove}
                  />
                ))
              : null}
            {selectionBBox ? (
              <SketchSelectionOverlay
                bbox={selectionBBox}
                showFrame={selectedFrames.length === 1}
                showHandles
                showDelete
                deleteLabel={deleteLabel}
                zoom={camera.zoom}
                onDelete={onDeleteSelected}
                onBodyPointerDown={startBodyMove}
                onHandlePointerDown={startResize}
              />
            ) : null}
          </div>
        ) : null}
        {styleValue && styleAnchor ? (
          <SketchTextPanel
            open
            mode={styleTargetText ? "text" : "arrowLabel"}
            anchor={styleAnchor}
            value={styleValue}
            labels={textStyleLabels}
            zoom={camera.zoom}
            onChange={(next) => {
              if (styleTargetText) {
                onTextsChange(
                  textsRef.current.map((t) =>
                    t.id === styleTargetText.id
                      ? {
                          ...t,
                          fill: next.fill,
                          bold: next.bold,
                          italic: false,
                          underline: false,
                          fontFamily: SKETCH_FONTS[0].value,
                        }
                      : t,
                  ),
                )
                onTextColorChange?.(next.fill)
                return
              }
              if (styleTargetArrow) {
                onArrowsChange(
                  arrowsRef.current.map((a) =>
                    a.id === styleTargetArrow.id
                      ? {
                          ...a,
                          color: next.fill,
                          labelBold: next.bold,
                          labelItalic: false,
                          labelUnderline: false,
                        }
                      : a,
                  ),
                )
              }
            }}
          />
        ) : null}
        {(tool === "pen" || tool === "eraser") && brushCursor && !disabled && !spaceHeld && !panning ? (
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute z-40",
              tool === "eraser" || penKind !== "highlighter" ? "rounded-full" : "rounded-xs",
              tool === "eraser"
                ? "border border-black/40 dark:border-white/50"
                : "border border-black/25 dark:border-white/30",
            )}
            style={{
              left: brushCursor.x,
              top: brushCursor.y,
              width:
                tool === "eraser"
                  ? eraserWidth
                  : penKind === "highlighter"
                    ? tipThicknessForWidth(brushWidth)
                    : brushWidth,
              height: tool === "eraser" ? eraserWidth : brushWidth,
              transform:
                tool === "pen" && penKind === "highlighter"
                  ? `translate(-50%, -50%) rotate(${brushAngle}rad)`
                  : "translate(-50%, -50%)",
              backgroundColor: tool === "eraser" ? "#ffffff" : withAlpha(color, brushOpacity),
              boxShadow: tool === "eraser" ? "0 0 0 1px rgb(0 0 0 / 0.2)" : "0 0 0 1px rgb(255 255 255 / 0.55)",
            }}
          />
        ) : null}
      </div>
      {zoomEnabled ? (
        <div data-sketch-zoom="" className="absolute bottom-2 left-2 z-50" onPointerDown={(e) => e.stopPropagation()}>
          <SelectDropdown
            value={zoomOverlay.value}
            onChange={(value) => {
              if (value === "fit" || value === "100" || value === "200") onZoomPreset(value)
            }}
            options={[
              { value: "fit", label: zoomOverlay.fitLabel },
              { value: "100", label: zoomOverlay.percent100Label },
              { value: "200", label: zoomOverlay.percent200Label },
            ]}
            placeholder={zoomOverlay.label}
            ariaLabel={zoomOverlay.menuAria}
            fitContent
            dropdownDirection="up"
            buttonClassName="h-7 bg-ui-bg-elevated/90 shadow-sm backdrop-blur-sm"
          />
        </div>
      ) : null}
    </div>
  )
}
